import { useEffect, useMemo, useState } from 'react';
import { ConfigProvider, Modal } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { addDays, isBefore, isSameDay, format } from 'date-fns';

import { earliestDeliveryFrom, buildDeliveryDays } from './slots';
import './index.scss';

/* Orders past the threshold in screens/Cart/deliverySchedule are delivered on an
   agreed date rather than sent out with the next run, so the client picks one
   before the order goes to the rep.

   Offered as a strip of days and a grid of times rather than a calendar and a
   clock: every button on screen is a delivery that can actually be met, so the
   client picks from what is available instead of proposing a moment and being
   told no. It also puts the whole choice on two taps at a size that survives a
   phone held in a workshop.

   The working day and the slot arithmetic live in ./slots so they can be
   exercised without mounting a modal - see slots.test.js. */
/* The screen is Arabic throughout; -u-nu-latn keeps the digits Western, which is
   how dates and prices are written everywhere else in the client. */
const DELIVERY_LOCALE = 'ar-EG-u-nu-latn';
/* Times are read on a phone in a workshop, where a 24-hour clock is a small sum
   to do at a glance, so the grid runs on a twelve hour clock and says which half
   of the day it means: "1:30 م".

   The marker is written here rather than taken from a date-fns locale - the
   library ships no Egyptian Arabic, and its one Arabic locale is Saudi, which is
   a whole bundle imported for two letters. Digits stay Western, as everywhere
   else in the client. */
const SLOT_TIME_FORMAT = 'h:mm';
const SLOT_MERIDIEM = { am: 'ص', pm: 'م' };

const formatSlotTime = (date) =>
  `${format(date, SLOT_TIME_FORMAT)} ${date.getHours() < 12 ? SLOT_MERIDIEM.am : SLOT_MERIDIEM.pm}`;

const DeliveryScheduleModal = ({
  visible,
  onCancel,
  onConfirm,
  confirmLoading,
  includesPendingOrders,
  submitError,
}) => {
  const { t } = useTranslation('cart');
  const [dayIndex, setDayIndex] = useState(0);
  const [slot, setSlot] = useState(null);
  const [error, setError] = useState('');

  /* Cleared on open rather than on close: destroyOnClose unmounts the Modal's
     children but not this component, so a cancelled selection used to come back
     pre-filled - and could by then be a moment already past. */
  useEffect(() => {
    if (visible) {
      setDayIndex(0);
      setSlot(null);
      setError('');
    }
  }, [visible]);

  /* Recomputed each time the dialog opens: this component is not unmounted in
     between - destroyOnClose only takes its children - so pinning it to mount
     would leave a client who sat on the cart for an hour with a slot that has
     since gone by. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const earliestDelivery = useMemo(() => earliestDeliveryFrom(new Date()), [visible]);

  const days = useMemo(() => buildDeliveryDays(earliestDelivery), [earliestDelivery]);

  /* Weekday and month names come from the browser rather than the translation
     file - they are the only calendar vocabulary the dialog needs, and keeping
     them out of ar.json means no list to maintain.

     Pinned to Arabic rather than read off i18n.language: en.json is empty and
     fallbackLng is 'ar', so every other string in this dialog is Arabic however
     the detector reads the browser. Following the detector here is what put
     "Wednesday" next to "يوم الاستلام". The latn subtag holds the digits to
     Western ones, which is what the rest of the screen uses. */
  const formatters = useMemo(
    () => ({
      weekday: new Intl.DateTimeFormat(DELIVERY_LOCALE, { weekday: 'long' }),
      date: new Intl.DateTimeFormat(DELIVERY_LOCALE, { day: 'numeric', month: 'short' }),
    }),
    [],
  );

  // "Today" and "tomorrow" are how a client says it; the weekday is for the rest.
  const dayName = (day) => {
    const today = new Date();
    if (isSameDay(day, today)) return t('delivery-today');
    if (isSameDay(day, addDays(today, 1))) return t('delivery-tomorrow');
    return formatters.weekday.format(day);
  };

  /* Falls back to the first day rather than trusting the index: reopening the
     dialog rebuilds the strip during render but only resets the index in the
     effect afterwards, so a day picked last time can briefly point past the end
     of a shorter strip. The highlight below reads the same figure, so the strip
     cannot show one day selected while the grid lists another's times. */
  const activeIndex = dayIndex < days.length ? dayIndex : 0;
  const activeDay = days[activeIndex];

  const handleConfirm = () => {
    if (!slot) {
      setError(t('delivery-required'));
      return;
    }
    /* The grid is built from this same figure, so nothing on screen should fail
       here - it stands as the guard for a dialog left open until its slots went
       stale, which is the one way a listed time can go by unbeaten. */
    if (isBefore(slot, earliestDelivery)) {
      setError(t('delivery-too-soon', { time: formatSlotTime(earliestDelivery) }));
      return;
    }
    setError('');
    onConfirm(slot);
  };

  const handleCancel = () => {
    setError('');
    onCancel();
  };

  /* The screen sets its direction per-view (see Landing and AuthLayout) and a
     Modal renders through a portal on document.body, so it does not inherit the
     cart's. Without this the strip ran today-on-the-left and the confirm button
     sat on the wrong side of an otherwise Arabic dialog. */
  return (
    <ConfigProvider direction="rtl">
      <Modal
        centered
        visible={visible}
        title={t('delivery-title')}
        okText={t('delivery-confirm')}
        cancelText={t('delivery-cancel')}
        onOk={handleConfirm}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        className="delivery-schedule-modal"
        destroyOnClose
      >
        {/* Say so when earlier orders are what pushed this over the threshold,
            otherwise the dialog looks wrong on a cart of two or three parts. */}
        <p className="delivery-schedule-modal__hint">
          {includesPendingOrders ? t('delivery-hint-combined') : t('delivery-hint')}
        </p>

        <div className="delivery-schedule-modal__label">{t('delivery-date')}</div>
        <div className="delivery-schedule-modal__days">
          {days.map(({ day }, index) => (
            <button
              key={day.getTime()}
              type="button"
              className={`delivery-schedule-modal__day${index === activeIndex ? ' is-selected' : ''}`}
              aria-pressed={index === activeIndex}
              onClick={() => {
                setDayIndex(index);
                /* The time carries the day it was picked on, so moving the day
                   drops it rather than quietly re-dating the client's choice. */
                setSlot(null);
                if (error) setError('');
              }}
            >
              <span className="delivery-schedule-modal__day-name">{dayName(day)}</span>
              <span className="delivery-schedule-modal__day-date">{formatters.date.format(day)}</span>
            </button>
          ))}
        </div>

        <div className="delivery-schedule-modal__label">{t('delivery-time')}</div>
        <div className="delivery-schedule-modal__slots">
          {activeDay &&
            activeDay.slots.map((option) => {
              const selected = !!slot && option.getTime() === slot.getTime();
              return (
                <button
                  key={option.getTime()}
                  type="button"
                  className={`delivery-schedule-modal__slot${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => {
                    setSlot(option);
                    if (error) setError('');
                  }}
                >
                  {formatSlotTime(option)}
                </button>
              );
            })}
        </div>

        {(error || submitError) && <div className="delivery-schedule-modal__error">{error || submitError}</div>}
      </Modal>
    </ConfigProvider>
  );
};

DeliveryScheduleModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmLoading: PropTypes.bool,
  includesPendingOrders: PropTypes.bool,
  submitError: PropTypes.string,
};

export default DeliveryScheduleModal;
